// src/components/home/HomeScreen.tsx

import { PageHeader } from "@nymbl/nos2-rules";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { HomeExpenseBanner } from "@/components/expenses/HomeExpenseBanner";
import { AttentionTiles } from "@/components/home/AttentionTiles";
import { CapacityPanel } from "@/components/home/CapacityPanel";
import { HomeActions } from "@/components/home/HomeActions";
import { KpiPanel } from "@/components/home/KpiPanel";
import { MonthTimePanel } from "@/components/home/MonthTimePanel";
import { PlanPanel } from "@/components/home/PlanPanel";
import { TrendPanel } from "@/components/home/TrendPanel";
import { MonthSwitcher } from "@/components/month/MonthSwitcher";
import { HomePtoBanner } from "@/components/pto/HomePtoBanner";
import { LogTimeDialog } from "@/components/time/LogTimeDialog";
import { useCurrentPerson } from "@/hooks/use-current-person";
import { useMonthSummary } from "@/hooks/use-month";
import { useBalances } from "@/hooks/use-pto";
import { useSignOffs } from "@/hooks/use-time-signoffs";
import { isPageBuilt, PAGE_HREFS } from "@/lib/features";
import { firstName } from "@/lib/first-name";
import { homeAlerts } from "@/lib/month/metrics";
import { browserTimeZone, type MonthKey, monthKeyOf, yearOf } from "@/lib/month/month";
import { vacationLeft } from "@/lib/pto/balances";
import { todayInZone } from "@/lib/time/dates";

/** Months in the utilization trend; the summary read also feeds the capacity card. */
const TREND_MONTHS = 6;

/** Two cards per row when they fit, one on phones. */
const ROW = "grid grid-cols-[repeat(auto-fit,minmax(min(100%,20rem),1fr))] gap-4";

export function HomeScreen() {
  const person = useCurrentPerson();
  const search = useSearch({ from: "/dashboard/" });
  const navigate = useNavigate({ from: "/dashboard/" });
  const today = todayInZone(new Date(), browserTimeZone());
  const current = monthKeyOf(today);
  const month = search.month ?? current;

  const summary = useMonthSummary(month, TREND_MONTHS);
  const signOffs = useSignOffs();
  const ptoBuilt = isPageBuilt(PAGE_HREFS.pto);
  // This year's balance; the read only runs once PTO is in the nav.
  const balances = useBalances(Number(today.slice(0, 4)), ptoBuilt);
  const [logKey, setLogKey] = useState<number | null>(null);

  const alerts = signOffs.data ? homeAlerts(signOffs.data.rows, today) : null;

  function setMonth(next: MonthKey) {
    void navigate({ search: { month: next === current ? undefined : next } });
  }

  /** A save already refreshes Home (the entry mutations invalidate the month reads). */
  function onLogOpenChange(open: boolean) {
    if (!open) setLogKey(null);
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={person.data ? `Hi ${firstName(person.data.name)}` : "Hi"}
        period={
          <MonthSwitcher
            month={month}
            current={current}
            min={summary.data?.firstMonth ?? null}
            max={summary.data?.lastMonth ?? null}
            onChange={setMonth}
          />
        }
        actions={
          <HomeActions
            expensesEnabled={person.data?.expensesEnabled ?? false}
            onLogTime={() => setLogKey(Date.now())}
            vacationLeft={ptoBuilt && balances.data ? vacationLeft(balances.data.rows) : null}
          />
        }
      />

      <HomePtoBanner />
      <HomeExpenseBanner enabled={person.data?.expensesEnabled ?? false} />

      <AttentionTiles alerts={alerts} />

      <div className={ROW}>
        <KpiPanel year={yearOf(month)} />
        <CapacityPanel month={month} query={summary} />
      </div>

      <div className={ROW}>
        <PlanPanel month={month} />
        <TrendPanel query={summary} />
      </div>

      <MonthTimePanel month={month} />

      {logKey !== null && (
        <LogTimeDialog
          key={logKey}
          open
          onOpenChange={onLogOpenChange}
          mode={{ kind: "new" }}
          defaultDate={today}
          today={today}
        />
      )}
    </div>
  );
}
